import { Controller } from '@hotwired/stimulus';

export class HeroStepsController extends Controller {
    static targets = ['button', 'circle', 'description'];

    declare readonly buttonTargets: HTMLButtonElement[];
    declare readonly circleTargets: HTMLElement[];
    declare readonly descriptionTarget: HTMLElement;

    connect(): void {
        this.clearActiveStep();
    }

    select(event: Event): void {
        const button = event.currentTarget;
        if (!(button instanceof HTMLButtonElement)) {
            return;
        }

        this.activateButton(button);
    }

    private activateButton(activeButton: HTMLButtonElement): void {
        if (activeButton.getAttribute('aria-pressed') === 'true') {
            this.clearActiveStep();
            return;
        }

        this.buttonTargets.forEach((button, index) => {
            const isActive = button === activeButton;
            const circle = this.circleTargets[index];
            button.setAttribute('aria-pressed', String(isActive));
            this.setCircleState(circle, isActive);
        });

        this.descriptionTarget.textContent = activeButton.dataset.heroStepsDescription ?? '';
        this.descriptionTarget.classList.remove('hidden');
    }

    private clearActiveStep(): void {
        this.buttonTargets.forEach((button, index) => {
            button.setAttribute('aria-pressed', 'false');
            this.setCircleState(this.circleTargets[index], false);
        });
        this.descriptionTarget.textContent = '';
        this.descriptionTarget.classList.add('hidden');
    }

    private setCircleState(circle: HTMLElement | undefined, isActive: boolean): void {
        if (!circle) {
            return;
        }

        const activeClasses = this.getStateClasses(circle, 'active', ['bg-green-600', 'text-white', 'scale-110']);
        const inactiveClasses = this.getStateClasses(circle, 'inactive', ['bg-green-100', 'text-green-700']);

        circle.classList.remove(...activeClasses, ...inactiveClasses);
        circle.classList.add(...(isActive ? activeClasses : inactiveClasses));
    }

    private getStateClasses(circle: HTMLElement, state: 'active' | 'inactive', fallback: string[]): string[] {
        const classList = circle.dataset[`heroSteps${state[0].toUpperCase()}${state.slice(1)}Classes`];
        return classList ? classList.split(/\s+/).filter(Boolean) : fallback;
    }
}
